import React, { useEffect, useRef } from 'react';
type PixelFieldProps = {
  className?: string;
  /** Size of each square pixel in CSS px */
  cell?: number;
  /**
   * Where the pixels gather. Styling is identical either way:
   * - 'bottom' (footer): density rises toward the bottom edge
   * - 'frame' (hero): density rises toward the side and bottom edges, leaving a clear pocket for the headline
   */
  layout?: 'bottom' | 'frame' | 'sides';
};

// Density weight (0–1) for a cell, per layout
function weightFor(layout: 'bottom' | 'frame' | 'sides', x: number, y: number, cols: number, rows: number) {
  const v = y / rows;
  if (layout === 'sides') {
    // Very quiet: only the outer ~18% on each side, capped low so it stays a background whisper
    const u = x / cols;
    const edge = Math.max(0, (Math.abs(u - 0.5) * 2 - 0.64) / 0.36);
    return edge * edge * 0.42;
  }
  if (layout === 'bottom') {
    // Starts lower and is capped below full strength so the footer stays understated
    const depth = Math.max(0, (v - 0.4) / 0.6);
    return depth * depth * 0.68;
  }
  const u = x / cols;
  const side = Math.max(0, (Math.abs(u - 0.5) * 2 - 0.35) / 0.65);
  const bottom = Math.max(0, (v - 0.55) / 0.45);
  return Math.min(1, side * side + bottom * bottom);
}
export const LEVELS = [0, 0.06, 0.12, 0.22, 0.36];
// Cursor influence: radius (in cells) and how far the pattern bends (in cells)
const WARP_RADIUS = 14;
const WARP_AMOUNT = 3.8;
// Smoothing for the cursor position and the influence fading in/out
const FOLLOW = 0.07;
const FADE = 0.05;

/**
 * A drifting, dithered grid of square pixels that densifies toward the bottom edge.
 * The cursor doesn't paint anything — it gently bends the existing pattern around it,
 * like a lens pushed through the field, and the warp eases in and out smoothly.
 */
export function PixelField({
  className = '',
  cell = 14,
  layout = 'bottom'
}: PixelFieldProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const host = canvas?.parentElement;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !host || !ctx) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let cols = 0;
    let rows = 0;
    let raf = 0;
    let visible = true;
    const start = performance.now();

    // Target vs smoothed cursor (in cell units) and warp strength
    const target = {
      x: 0,
      y: 0
    };
    const smooth = {
      x: 0,
      y: 0
    };
    let hovering = false;
    let strength = 0;
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const {
        width,
        height
      } = host.getBoundingClientRect();
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cols = Math.ceil(width / cell);
      rows = Math.ceil(height / cell);
    };

    // Cheap layered-sine noise — smooth, organic clouds without a noise library
    const field = (x: number, y: number, t: number) => (Math.sin(x * 0.21 + t * 0.6) + Math.sin(y * 0.27 - t * 0.4) + Math.sin((x + y) * 0.13 + t * 0.3) + Math.sin(Math.hypot(x - cols * 0.7, y - rows) * 0.18 - t * 0.8)) / 4;
    const draw = (now: number) => {
      const t = reduce ? 0 : (now - start) / 1000;

      // Ease the cursor and the warp strength so nothing ever snaps
      smooth.x += (target.x - smooth.x) * FOLLOW;
      smooth.y += (target.y - smooth.y) * FOLLOW;
      strength += ((hovering && !reduce ? 1 : 0) - strength) * FADE;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#ffffff';
      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          const weight = weightFor(layout, x, y, cols, rows);
          if (weight === 0) continue;
          let sx = x;
          let sy = y;
          if (strength > 0.001) {
            const dx = x - smooth.x;
            const dy = y - smooth.y;
            const d = Math.hypot(dx, dy);
            if (d < WARP_RADIUS && d > 0.0001) {
              // Smooth falloff; sample the field from closer to the cursor so the pattern swells outward
              const f = Math.pow(1 - d / WARP_RADIUS, 2) * strength * WARP_AMOUNT;
              sx -= dx / d * f;
              sy -= dy / d * f;
            }
          }
          const n = (field(sx, sy, t) + 1) / 2;
          const level = LEVELS[Math.min(LEVELS.length - 1, Math.floor(n * weight * LEVELS.length * 1.6))];
          if (level > 0) {
            ctx.globalAlpha = level;
            ctx.fillRect(x * cell, y * cell, cell - 1, cell - 1);
          }
        }
      }
      ctx.globalAlpha = 1;
      if (visible) raf = requestAnimationFrame(draw);
    };

    // Listen on the window (the field may sit behind other content) and map into the canvas box
    const onMove = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) {
        hovering = false;
        return;
      }
      target.x = (e.clientX - rect.left) / cell;
      target.y = (e.clientY - rect.top) / cell;
      if (!hovering) {
        // Start the warp where the cursor enters rather than sliding in from elsewhere
        if (strength < 0.01) {
          smooth.x = target.x;
          smooth.y = target.y;
        }
        hovering = true;
      }
    };
    const onLeave = () => {
      hovering = false;
    };
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      cancelAnimationFrame(raf);
      if (visible) raf = requestAnimationFrame(draw);
    });
    const ro = new ResizeObserver(resize);
    resize();
    ro.observe(host);
    io.observe(host);
    window.addEventListener('pointermove', onMove, {
      passive: true
    });
    document.addEventListener('pointerleave', onLeave);
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerleave', onLeave);
    };
  }, [cell, layout]);
  return <canvas ref={canvasRef} aria-hidden="true" className={`pixel-anim pointer-events-none absolute inset-0 ${className}`} data-id="element-293" data-mp="3f7trxdcfiso7" />;
}